import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { USER_CHANNEL_SUBSCRIPTIONS } from '../../../gql/users';
import { isSubscriptionActive } from '../../../utils/channelSubscriptions.utils';
import {
    Button,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { CallToActionCard } from '../../ui/CallToActionCard';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { CALL_TO_ACTION } from '../../../config';
import { PremiumChannels } from './ChannelsGrid/PremiumChannels';
import { FreeChannels } from './ChannelsGrid/FreeChannels';
import ChannelPreviewList from '../../ui/ChannelPreviewList';
import { ChannelSubscription } from '../../../shared/types';
import { useHistory } from 'react-router-dom';
import ArticleList from '../../ui/ArticleList';
import { ContainerNoScroll } from '../../ui/ContainerNoScroll';
import Loader from '../../ui/LoadingIndicator';

const ForYouPage: React.FC = () => {
    const history = useHistory();
    const theme = useTheme();
    const xsDown = useMediaQuery(theme.breakpoints.down('sm'));

    const [myArticlesCount, setMyArticlesCount] = useState<number | undefined>(
        undefined
    );
    const [articleCount, setArticleCount] = useState<number | undefined>(
        undefined
    );

    const queryChannelSubscriptions = useQuery(USER_CHANNEL_SUBSCRIPTIONS, {
        fetchPolicy: 'network-only',
    });

    const activeChannelSubscriptions: ChannelSubscription[] = useMemo(() => {
        if (!queryChannelSubscriptions.data) return [];

        return queryChannelSubscriptions.data.me.channelSubscriptions.filter(
            isSubscriptionActive
        );
    }, [queryChannelSubscriptions.data]);

    if (queryChannelSubscriptions.loading) {
        return <Loader minHeight={160} />;
    }

    const haveSubscriptions = activeChannelSubscriptions.length > 0;

    return (
        <ContainerNoScroll>
            <Grid container spacing={6}>
                <Grid size={12}>
                    <Typography variant="h4" component="h1">
                        All the latest market insights, tailored for you.
                    </Typography>
                </Grid>
                {haveSubscriptions && (
                    <>
                        {(myArticlesCount === undefined ||
                            myArticlesCount > 0) && (
                            <Grid size={12}>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Typography variant="h5" component="h2">
                                            From your subscriptions
                                        </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        <ArticleList
                                            source="forYou"
                                            showPagination={false}
                                            onlyMySubscriptions
                                            onDataLoaded={(t) =>
                                                setMyArticlesCount(t)
                                            }
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        )}
                        <Grid size={12}>
                            <Grid container spacing={2}>
                                <Grid size={12}>
                                    <Grid
                                        container
                                        spacing={2}
                                        sx={{
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 'grow',
                                            }}
                                        >
                                            <Typography
                                                variant="h5"
                                                component="h2"
                                            >
                                                Your channel subscriptions
                                            </Typography>
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                sm: 'auto',
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                fullWidth={xsDown}
                                                onClick={() =>
                                                    history.push(
                                                        baseUrls.subscriptions
                                                    )
                                                }
                                            >
                                                Manage subscriptions
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid size={12}>
                                    <ChannelPreviewList
                                        channels={activeChannelSubscriptions.map(
                                            (cs) => cs.channel
                                        )}
                                        loading={false}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                )}
                {(articleCount === undefined || articleCount > 0) && (
                    <Grid size={12}>
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <Typography variant="h5" component="h2">
                                    Latest free articles
                                </Typography>
                            </Grid>
                            <Grid size={12}>
                                <ArticleList
                                    source="forYou"
                                    showPagination={false}
                                    freeOnly
                                    excludeMySubscriptions
                                    onDataLoaded={(t) => setArticleCount(t)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                )}
                {!haveSubscriptions && (
                    <Grid size={12}>
                        <CallToActionCard
                            title="Stay ahead of the curve"
                            subtitle="Channels is a dedicated space for our expert contributors to share their insights and market commentary, whilst ensuring that you can access their content from one central platform."
                            buttonText="Browse channels"
                            buttonLink={baseUrls.channels.main}
                            imageUrl={CALL_TO_ACTION}
                        />
                    </Grid>
                )}
                <Grid size={12}>
                    <PremiumChannels randomize quantityToDisplay={3} />
                </Grid>
                <Grid size={12}>
                    <FreeChannels randomize quantityToDisplay={3} />
                </Grid>
            </Grid>
        </ContainerNoScroll>
    );
};

export default ForYouPage;
